// =========================
// 🔝 ALL IMPORTS AT TOP
// =========================
import React, { useState, useEffect, useRef } from "react";
import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";

// GSAP plugins register must be after import, but still at top
gsap.registerPlugin(SplitText);

// =========================
// 🔰 COMPONENT START
// =========================
export default function Login() {

  // MAIN LOGIN STATES
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [loginUser, setLoginUser] = useState("");
  // 🔹 Input change handler
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // 🔹 Submit handler
  const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    const res = await axios.post("https://api.speedfollowinfo.com/api/auth/login", {
      username: form.username,
      password: form.password,
    });

    if (res.data.success) {
      const user = res.data.user;

      // 🔹 Save full user object including referral info
      localStorage.setItem("token", res.data.token);
      localStorage.setItem(
        "user",
        JSON.stringify({
          id: user.id,
          username: user.username,
          email: user.email,
          balance: user.balance || 0,
          spend: user.spend || 0,
          role: user.role || "user",
          referralCode: user.referralCode || "GUEST",
          earnedBalance: user.earnedBalance || 0,
          totalReferrals: user.totalReferrals || 0,
          status: user.status || "active",
        })
      );

      // Suspended block
      if (user.status === "suspended") {
        setTimeout(() => {
          window.location.replace("/account-suspended");
        }, 80);
        return;
      }

      // Username for popup
      setLoginUser(user.username);

      // Success screen show
      setShowSuccess(true);

      // Redirect
      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 1800);
    }
  } catch (err) {
    console.error("Login Error:", err);

    // 🔥 Hacker Style ACCESS DENIED Popup
    const popup = document.createElement("div");
    popup.innerHTML = `
      <div style="
        position: fixed; inset: 0;
        background: rgba(0,0,0,0.88);
        display: flex; align-items: center; justify-content: center;
        z-index: 999999; animation: fadeIn 0.25s ease-out;
      ">
        <div style="
          background: linear-gradient(145deg,#2b0000,#000000);
          padding: 35px; width: 90%; max-width: 420px;
          border-radius: 18px; text-align: center;
          border: 2px solid #ff1a1a;
          box-shadow: 0 0 25px #ff0000;
          animation: shake 0.4s ease-out;
        ">
          <div style="font-size: 80px; color:#ff1a1a; margin-bottom:10px;">
            🚨
          </div>

          <h2 style="
            color:#ff2e2e; font-size:28px; font-weight:bold;
            text-shadow:0 0 12px #ff0000; margin-bottom:5px;">
            ACCESS DENIED
          </h2>

          <p style="
            color:#ffb3b3; font-size:17px; line-height:1.4;
            margin-bottom:20px;">
            Wrong <b>Username</b> or <b>Password</b> Attempt!  
            আরেকবার ভুল করলে <b style="color:#ff6666;">Firewall Lock</b> লেগে যাবে 🔥
          </p>

          <button style="
            width:100%; background:#ff0000; border:none;
            padding:12px 20px; color:white; font-size:18px;
            font-weight:bold; border-radius:10px;
            cursor:pointer; box-shadow:0 0 18px #ff1a1a;">
            Try Again
          </button>
        </div>
      </div>

      <style>
        @keyframes fadeIn { from{opacity:0;} to{opacity:1;} }
        @keyframes shake {
          0%{ transform:translateX(0); }
          25%{ transform:translateX(-8px); }
          50%{ transform:translateX(8px); }
          75%{ transform:translateX(-5px); }
          100%{ transform:translateX(0); }
        }
      </style>
    `;

    popup.querySelector("button").onclick = () => popup.remove();
    document.body.appendChild(popup);
  }
};

  return (
  <div className="relative min-h-screen bg-[#0b0f1a] text-white">

    {/* ============================
        🟦 REAL LOGIN PAGE CONTENT
    ============================= */}
    <div className="min-h-screen flex flex-col lg:flex-row items-center justify-center px-6 lg:px-20 py-10">

      {/* 🎉 SUCCESS SCREEN */}
      {showSuccess && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999]
                        backdrop-blur-xl bg-black/40 animate-fadeIn">

          <div className="bg-white/10 backdrop-blur-2xl border border-white/20
                          shadow-2xl p-10 rounded-3xl text-center
                          animate-scaleIn w-[90%] max-w-md">

            <div className="text-6xl mb-4 animate-pop">✅</div>

            <h2 className="text-3xl font-extrabold text-[#00ffb3] drop-shadow-lg mb-1">
              Login Successful
            </h2>

            <h3 className="text-2xl font-bold text-white mb-1">
              Welcome Back, <span className="text-[#00eaff]">{loginUser}</span>
            </h3>

            <p className="mt-3 text-gray-200 text-lg">
              Redirecting to Dashboard...
            </p>
          </div>

          {/* Animation CSS */}
          <style>{`
            @keyframes fadeIn { from {opacity:0;} to {opacity:1;} }
            @keyframes scaleIn {
              0% { transform: scale(0.7); opacity:0; }
              100% { transform: scale(1); opacity:1; }
            }
            @keyframes pop {
              0% { transform: scale(0.6); }
              80% { transform: scale(1.2); }
              100% { transform: scale(1); }
            }
            .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
            .animate-scaleIn { animation: scaleIn 0.35s ease-out; }
            .animate-pop { animation: pop 0.45s ease-out; }
          `}</style>
        </div>
      )}

      {/* LEFT SIDE */}
      <div className="w-full lg:w-1/2 text-center lg:text-left mb-10 lg:mb-0">
        <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 leading-tight">
          <span className="text-[#8b5cf6]">Speed</span>{" "}
          <span className="text-[#facc15]">Follow Info</span>
        </h1>
        <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-100">
          Panel in Bangladesh
        </h2>
        <p className="text-gray-400 text-sm sm:text-base max-w-md mx-auto lg:mx-0">
          Fastest & Cheapest SMM Panel for Growing Your Social Media Presence.
        </p>

        <button
          onClick={() => navigate("/register")}
          className="mt-6 bg-gradient-to-r from-[#8b5cf6] to-[#4f46e5]
                     px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition"
        >
          Sign Up Now
        </button>
      </div>

      {/* RIGHT SIDE LOGIN FORM */}
      <div className="w-full lg:w-1/2 max-w-md bg-[#161c34] p-8 rounded-2xl shadow-2xl">

        <h2 className="text-3xl font-bold text-center text-[#facc15] mb-6">
          Login To Speed Follow Info
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Username */}
          <div>
            <label className="block text-gray-300 mb-1">Username</label>
            <input
              type="text"
              name="username"
              placeholder="Enter your username"
              value={form.username}
              onChange={handleChange}
              required
              className="w-full bg-[#1b2142] text-white border border-[#2f3b6c]
                         rounded-lg p-3 focus:ring-2 focus:ring-[#8b5cf6]"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-gray-300 mb-1">Password</label>
            <input
              type="password"
              name="password"
              placeholder="Enter your password"
              value={form.password}
              onChange={handleChange}
              required
              className="w-full bg-[#1b2142] text-white border border-[#2f3b6c]
                         rounded-lg p-3 focus:ring-2 focus:ring-[#8b5cf6]"
            />
          </div>

          {/* Login Button */}
          <button
            type="submit"
            className="w-full bg-gradient-to-r from-[#8b5cf6] to-[#4f46e5]
                       text-white py-3 rounded-lg font-semibold hover:opacity-90 transition"
          >
            Login To Dashboard
          </button>
        </form>

        <div className="text-center mt-4 text-gray-400 text-sm">
          New here?{" "}
          <Link to="/register" className="text-[#facc15] font-semibold hover:underline">
            Create an account
          </Link>
        </div>

      </div>
    </div>
    </div>
  );
}